import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { z } from 'zod';
import { FormField, Label, Input, InlineMessage } from '@appcues/sonar';
import {
  Button,
  cn,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
} from '@appcues/sonar-builder';
import { zodToFormikValidate } from 'ext/lib/zod-formik';

import { userPropertiesShape } from 'ext/entities/user-properties';
import { LANGUAGES } from 'ext/lib/localization';

import { Form } from './styled';
import { Select } from './CustomSelect';

const DEFAULT_PROPERTY = {
  value: '_lastBrowserLanguage',
  label: 'Last Browser Language',
};

const LANGUAGES_OPTIONS = LANGUAGES.map(({ id, label }) => ({
  value: id,
  label,
}));

const handleTargetCriteriaOptions = (userProperties, userPropertyValue) => {
  const userProperty = userProperties?.find(
    ({ value }) => value === userPropertyValue
  );

  const userPropertyOptions =
    userProperty?.options?.map(({ name }) => ({ value: name, label: name })) ??
    [];

  return userPropertyValue === DEFAULT_PROPERTY.value
    ? LANGUAGES_OPTIONS
    : userPropertyOptions;
};

export const AddLocale = ({
  overrideSelect = false,
  userProperties,
  setIsAddingLanguage,
  isEditingLanguage,
  setEditingLanguage,
  onCreateLocale,
  onUpdateLocale,
  locales = [],
}) => {
  const targetCriteriaOptions = useRef([]);

  const propertyOptions = useMemo(
    () =>
      userProperties?.map(({ value, name }) => ({
        value,
        label: name,
      })),
    [userProperties]
  );

  const updateTargetCriteriaOptions = property => {
    const targetCriteriaEditingOptions = handleTargetCriteriaOptions(
      userProperties,
      property.value
    );
    targetCriteriaOptions.current = targetCriteriaEditingOptions;
    return targetCriteriaEditingOptions;
  };

  const getInitialValues = () => {
    if (!isEditingLanguage) {
      updateTargetCriteriaOptions(DEFAULT_PROPERTY);

      return {
        languageName: '',
        property: DEFAULT_PROPERTY,
        targetCriteria: [],
      };
    }

    const property =
      propertyOptions?.find(
        ({ label }) => label === isEditingLanguage.property
      ) || DEFAULT_PROPERTY;

    const targetCriteriaEditingOptions = updateTargetCriteriaOptions(property);

    const targetCriteria = targetCriteriaEditingOptions.filter(({ label }) =>
      isEditingLanguage.targetCriteria.includes(label)
    );

    return {
      languageName: isEditingLanguage?.name || '',
      property,
      targetCriteria,
    };
  };

  const onCancelClick = () => {
    setIsAddingLanguage(false);

    if (isEditingLanguage) {
      setEditingLanguage();
    }
  };

  const LanguageSchema = z.object({
    languageName: z
      .string()
      .min(1, 'This field is required.')
      .refine(
        name => {
          if (isEditingLanguage) return true;
          return !locales?.some(
            ({ name: localeName }) =>
              localeName?.toLowerCase() === name?.toLowerCase()
          );
        },
        {
          message: 'Language already exists!',
        }
      ),
    targetCriteria: z.array(z.any()).min(1, 'This field is required.'),
    property: z.any().optional(),
  });

  const onSubmit = values => {
    const { languageName: name, property, targetCriteria: criteria } = values;

    const locale = {
      name,
      conditions: {
        and: [
          {
            properties: {
              property: property.value,
              operator: 'in',
              value: criteria.map(item => item.value).join('\n'),
            },
          },
        ],
      },
    };

    setIsAddingLanguage(false);

    if (isEditingLanguage) {
      onUpdateLocale(isEditingLanguage.id, locale);
      setEditingLanguage();
      return;
    }

    onCreateLocale(locale);
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      onSubmit={onSubmit}
      validate={zodToFormikValidate(LanguageSchema)}
    >
      {({
        handleBlur,
        handleChange,
        values,
        handleSubmit,
        errors,
        dirty,
        isValid,
        setFieldValue,
        setTouched,
        touched,
      }) => (
        <Form onSubmit={handleSubmit}>
          <SheetHeader>
            <SheetTitle>
              {isEditingLanguage ? 'Edit Language' : 'Add Language'}
            </SheetTitle>
          </SheetHeader>
          <SheetBody className={cn('px-6 py-2')}>
            <FormField>
              <Label htmlFor="languageName">Language name</Label>
              <Input
                aria-label="language name"
                id="languageName"
                name="languageName"
                placeholder="e.g. English"
                value={values.languageName}
                error={errors.languageName && touched.languageName}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {errors.languageName && touched.languageName && (
                <InlineMessage variant="error">
                  {errors.languageName}
                </InlineMessage>
              )}
            </FormField>
            <FormField>
              <Label htmlFor="property">Property</Label>
              <Select
                aria-label="Property"
                classNamePrefix={overrideSelect && 'react-select'}
                id="property"
                inputId="property"
                name="property"
                value={values.property}
                options={propertyOptions}
                onChange={prop => {
                  if (!prop) return;
                  setFieldValue('property', prop);
                  setFieldValue('targetCriteria', []);
                  updateTargetCriteriaOptions(prop);
                }}
              />
            </FormField>
            <FormField>
              <Label htmlFor="targetCriteria">Targeting Criteria</Label>
              <Select
                aria-label="Targeting Criteria"
                classNamePrefix={overrideSelect && 'react-select'}
                id="targetCriteria"
                inputId="targetCriteria"
                name="targetCriteria"
                placeholder="Select target criteria"
                value={values.targetCriteria}
                onChange={targetValues => {
                  setTouched({ targetCriteria: true });
                  setFieldValue('targetCriteria', targetValues);
                }}
                options={targetCriteriaOptions.current}
                isClearable
                isMulti
              />
              {errors.targetCriteria && touched.targetCriteria && (
                <InlineMessage variant="error">
                  {errors.targetCriteria}
                </InlineMessage>
              )}
            </FormField>
          </SheetBody>
          <SheetFooter className={cn('grid grid-cols-2 gap-2')}>
            <Button onClick={onCancelClick} variant="ghost" fullWidth>
              Back
            </Button>
            <Button
              disabled={!isValid || !dirty}
              type="submit"
              variant="primary"
              fullWidth
            >
              Save
            </Button>
          </SheetFooter>
        </Form>
      )}
    </Formik>
  );
};

AddLocale.propTypes = {
  overrideSelect: PropTypes.bool,
  userProperties: userPropertiesShape,
  setIsAddingLanguage: PropTypes.func,
  isEditingLanguage: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    property: PropTypes.string,
    targetCriteria: PropTypes.arrayOf(PropTypes.string),
  }),
  setEditingLanguage: PropTypes.func,
  onCreateLocale: PropTypes.func,
  onUpdateLocale: PropTypes.func,
  locales: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      property: PropTypes.string,
      targetCriteria: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};
